import { Calendar } from '@fullcalendar/core';
import dayGridMonth from '@fullcalendar/daygrid';
import timeGrid from '@fullcalendar/timegrid';
import interaction from '@fullcalendar/interaction';
import sweetAlert from 'sweetalert';
import moment from 'moment-timezone';
import { Turbo } from '@hotwired/turbo-rails';
import FlashMessage from './flash_message';
import SidePanel from './sidepanel';
import { locale } from '../../calendar/locales';
import '@fullcalendar/core/main.css';
import '@fullcalendar/daygrid/main.css';
import '@fullcalendar/timegrid/main.css';

const SCROLL_TO_TIME = '07:00';
const MIN_TIME = '06:00:00';
const MAX_TIME = '22:00:00';

// Slot duration / label interval for each named scale.
// Week view supports 1h and 30m; day view adds 15m.
const SCALE_CONFIG = {
  '1h':  { slotDuration: '01:00:00', slotLabelInterval: '01:00:00' },
  '30m': { slotDuration: '00:30:00', slotLabelInterval: '01:00:00' },
  '15m': { slotDuration: '00:15:00', slotLabelInterval: '00:30:00' }
};
// Returns a { minTime, maxTime } object for the time-grid based on the schedule
// activities visible in [viewStart, viewEnd), or null if there are none.
// Only events with activity_type === 'schedule' influence the time window;
// registration and lead activities are intentionally excluded.
function computeDynamicTimeRange(events, viewStart, viewEnd) {
  const scheduleEvents = events.filter(ev => {
    if (!ev.extendedProps || ev.extendedProps.activity_type !== 'schedule') return false;
    if (!ev.start || !ev.end) return false;
    return ev.start < viewEnd && ev.end > viewStart;
  });

  if (scheduleEvents.length === 0) return null;

  let minMins = 24 * 60;
  let maxMins = 0;

  scheduleEvents.forEach(ev => {
    const startMins = ev.start.getHours() * 60 + ev.start.getMinutes();
    const endMins = ev.end.getHours() * 60 + ev.end.getMinutes();
    minMins = Math.min(minMins, startMins);
    // Midnight end (00:00) represents end-of-day, treat as 24:00
    maxMins = Math.max(maxMins, endMins === 0 ? 24 * 60 : endMins);
  });

  // Pad by 1 hour, round to nearest 30 min, clamp to valid day bounds [0, 24h]
  const paddedMin = Math.max(0, Math.floor((minMins - 60) / 30) * 30);
  const paddedMax = Math.min(24 * 60, Math.ceil((maxMins + 60) / 30) * 30);
  // Apply minimum visible window: never show less than 6:00 AM → 9:00 PM.
  // The range can expand beyond these bounds for early/late activities.
  const FLOOR_MIN = 6 * 60;   // 06:00
  const FLOOR_MAX = 21 * 60;  // 21:00
  const roundedMin = Math.min(paddedMin, FLOOR_MIN);
  const roundedMax = Math.max(paddedMax, FLOOR_MAX);

  const toTimeStr = mins => {
    const h = Math.floor(mins / 60).toString().padStart(2, '0');
    const m = (mins % 60).toString().padStart(2, '0');
    return `${h}:${m}:00`;
  };

  return { minTime: toTimeStr(roundedMin), maxTime: toTimeStr(roundedMax) };
}

const EventPartsCalendar = {
  start() {
    const $calendar = $('.js-event-parts-calendar');
    if (!$calendar.length) {
      return;
    }
    this.setupCalendar($calendar);
    this.setVisibilityForActionbar($calendar);
    this.watchActionbarClicks($calendar);
  },

  setVisibilityForActionbar($calendar) {
    const { eventId } = $calendar.data();
    const selectedView = window.localStorage.getItem(`fcActivitiesView-event-${eventId}`);
    if (selectedView === 'calendar') {
      $('.js-show-calendar').addClass('is-on').removeClass('is-off');
      $($('.js-show-calendar').data('hideOnClick')).addClass('is-hidden');
      $('.js-show-event-parts-list').addClass('is-off').removeClass('is-on');
      $('.js-calendar-container').removeClass('is-hidden');
      $('.js-list').addClass('is-hidden');
      $calendar.trigger('resize');
    } else {
      $('.js-show-calendar').addClass('is-off').removeClass('is-on');
      $('.js-show-event-parts-list').addClass('is-on').removeClass('is-off');
      $($('.js-show-event-parts-list').data('showOnClick')).removeClass('is-hidden');
      $('.js-calendar-container').addClass('is-hidden');
      $('.js-list').removeClass('is-hidden');
      $('.js-scale-control').addClass('is-hidden');
    }
  },

  watchActionbarClicks($calendar) {
    const { eventId } = $calendar.data();
    $('.js-show-calendar').on('click', () => {
      $calendar.trigger('resize');
      window.localStorage.setItem(`fcActivitiesView-event-${eventId}`, 'calendar');
      const savedFcView = window.localStorage.getItem(`fcDefaultView-event-${eventId}`) || 'timeGridWeek';
      if (savedFcView === 'timeGridWeek' || savedFcView === 'timeGridDay') {
        $('.js-scale-control').removeClass('is-hidden');
      }
    });
    $('.js-show-event-parts-list').on('click', () => {
      window.localStorage.setItem(`fcActivitiesView-event-${eventId}`, 'list');
      $('.js-scale-control').addClass('is-hidden');
      if ($calendar.data('reloadListView')) {
        Turbo.visit(window.location, { action: 'replace' });
      }
    });
  },

  setupCalendar($calendar) {
    let search = '';
    const selectedTagIds = new Set();
    let debounce = null;
    let duplicatedEvent = null;
    // Tracks the activeStart of the last-rendered week view for smart week→day navigation.
    let lastWeekViewStart = null;
    // Guards against re-entrant gotoDate calls during week→day redirection.
    let navigatingToDay = false;
    // Tracks the currently applied time bounds so we can skip no-op setOption calls.
    let currentMinTime = MIN_TIME;
    let currentMaxTime = MAX_TIME;
    const {
      defaultDate, url, newEventPartUrl, offsetChangeUrl, duplicateUrl, updateUrl, editUrl, eventId
    } = $calendar.data();
    // Activities visible in the most recently loaded week view, sorted by start time.
    // Populated by the loading callback; consumed by the Week→Day navigation in datesRender.
    let weekEvents = [];
    // True only on the very first calendar open for this event (no saved date in localStorage).
    // The loading callback uses it to jump to the earliest activity's week on first render.
    let autoNavigateToFirstActivity = !window.localStorage.getItem(`fcDefaultDate-event-${eventId}`);

    // Per-view scale persisted in localStorage. Week supports 1h/30m; day adds 15m.
    // Default both to '1h' for a clean, uncluttered initial view.
    const weekScaleKey = `fcWeekScale-event-${eventId}`;
    const dayScaleKey = `fcDayScale-event-${eventId}`;
    const getWeekScale = () => window.localStorage.getItem(weekScaleKey) || '1h';
    const getDayScale = () => window.localStorage.getItem(dayScaleKey) || '1h';
    const { lang } = document.documentElement;
    const calendar = new Calendar($calendar.get(0), {
      locale: locale(lang),
      plugins: [interaction, dayGridMonth, timeGrid],
      header: {
        left: 'prev,next today',
        center: 'title',
        right: 'dayGridMonth,timeGridWeek,timeGridDay'
      },
      defaultDate: (window.localStorage.getItem(`fcDefaultDate-event-${eventId}`) || defaultDate),
      defaultView: (window.localStorage.getItem(`fcDefaultView-event-${eventId}`) || 'timeGridWeek'),
      allDaySlot: false,
      selectable: true,
      editable: false,
      // Limit visible time range to practical daytime hours. Eliminates dead overnight
      // space in week/day views without hiding any realistic activity schedule.
      // Future: expose as user preference or derive from event's typical schedule window.
      minTime: MIN_TIME,
      maxTime: MAX_TIME,
      // Makes day/week column headers clickable links to that day's day view.
      // Provides an intuitive Week→Day drill-down path without relying solely on
      // the toolbar Day button (which defaults to the week's first day).
      navLinks: true,
      events: { url },
      unselectCancel: '.sidepanel',
      loading(isLoading) {
        if (isLoading) return;
        const { view } = calendar;

        // After each week-view fetch, cache the visible activities sorted by start
        // time. The datesRender handler reads this when the user presses Day view,
        // so it can jump to the first activity date rather than a blank day.
        if (view.type === 'timeGridWeek') {
          weekEvents = calendar.getEvents()
            .filter(e => e.start >= view.activeStart && e.start < view.activeEnd)
            .sort((a, b) => a.start - b.start);
        }

        // In day view, scroll to a sensible position based on the first visible
        // activity rather than always using the hardcoded SCROLL_TO_TIME.
        // Buffer = 30 min so there is context above the first event.
        if (view.type === 'timeGridDay') {
          const dayEvents = calendar.getEvents()
            .filter(e => e.start >= view.activeStart && e.start < view.activeEnd)
            .sort((a, b) => a.start - b.start);
          if (dayEvents.length > 0) {
            const firstStart = dayEvents[0].start;
            const bufferMs = 30 * 60 * 1000;
            const scrollTarget = new Date(Math.max(firstStart.getTime() - bufferMs, 0));
            const hh = String(scrollTarget.getHours()).padStart(2, '0');
            const mm = String(scrollTarget.getMinutes()).padStart(2, '0');
            setTimeout(() => calendar.scrollToTime(`${hh}:${mm}`), 0);
          }
        }

        // On the very first open (no saved date preference), navigate to the
        // earliest activity's date if it is not already in the visible range.
        // This corrects for @initial_date being based on an unordered DB query,
        // which can point to the wrong week when the event has multiple activities.
        if (autoNavigateToFirstActivity) {
          autoNavigateToFirstActivity = false;
          const events = calendar.getEvents();
          if (events.length > 0) {
            const earliest = events.reduce((min, e) => (e.start < min.start ? e : min));
            const { activeStart, activeEnd } = view;
            if (earliest.start < activeStart || earliest.start >= activeEnd) {
              setTimeout(() => calendar.gotoDate(earliest.start), 0);
            }
          }
        }
      },
      views: {
        timeGridWeek: {
          slotDuration: SCALE_CONFIG[getWeekScale()].slotDuration,
          slotLabelInterval: SCALE_CONFIG[getWeekScale()].slotLabelInterval
        },
        timeGridDay: {
          slotDuration: SCALE_CONFIG[getDayScale()].slotDuration,
          slotLabelInterval: SCALE_CONFIG[getDayScale()].slotLabelInterval
        }
      },
      datesRender(info) {
        const { view: { type, activeStart, activeEnd } } = info;
        // Use public getDate() API rather than the internal calendar.state object.
        const currentDate = calendar.getDate();
        window.localStorage.setItem(`fcDefaultView-event-${eventId}`, type);
        window.localStorage.setItem(`fcDefaultDate-event-${eventId}`, currentDate.toISOString());

        // Show/hide scale control and sync active button state.
        // Scale control is only meaningful for time-grid views.
        if (type === 'timeGridWeek' || type === 'timeGridDay') {
          const activeScale = type === 'timeGridWeek' ? getWeekScale() : getDayScale();
          $('.js-scale-control').removeClass('is-hidden');
          // 15m button is only available in day view.
          $('.js-scale-15m').toggleClass('is-hidden', type !== 'timeGridDay');
          $('.js-scale-btn').each(function updateScaleBtn() {
            const btnScale = $(this).data('scale');
            $(this).toggleClass('is-on', btnScale === activeScale)
                   .toggleClass('is-off', btnScale !== activeScale);
          });
        } else {
          $('.js-scale-control').addClass('is-hidden');
        }

        // Keep track of the week's start date so we can make a smarter day-view
        // target if the user presses the Day toolbar button.
        if (type === 'timeGridWeek') {
          lastWeekViewStart = activeStart;
        }

        // Scroll to business hours on every time-grid render and navigation.
        // For day view this acts as a fallback; when activities are present the
        // loading callback fires afterward and overrides with an activity-aware
        // scroll position.
        if (type === 'timeGridWeek' || type === 'timeGridDay') {
          setTimeout(() => calendar.scrollToTime(SCROLL_TO_TIME), 0);
          // Adjust visible time range based on schedule activities in this range.
          // Uses events already in memory; loading() handles the async-fetch case.
          updateTimeRange(activeStart, activeEnd);
        }

        // When the user presses Day view from week view, choose the best day to land on:
        //   1. The earliest activity date visible in that week (primary case).
        //   2. If no activities in the week: today (if within the week) or Monday
        //      rather than defaulting to the week-start day (often a blank Sunday).
        // Guard: only fires when coming from week view (lastWeekViewStart set).
        if (type === 'timeGridDay' && !navigatingToDay && lastWeekViewStart !== null) {
          let target = null;

          if (weekEvents.length > 0) {
            // Use the first activity's calendar date (strip time component).
            const first = weekEvents[0].start;
            target = new Date(first.getFullYear(), first.getMonth(), first.getDate());
          } else if (currentDate.getDay() === 0) {
            // No activities in the week and FC landed on Sunday: prefer today
            // (if it falls in this week) or Monday over a blank weekend day.
            const weekEnd = new Date(lastWeekViewStart.getTime() + 7 * 24 * 60 * 60 * 1000);
            const today = new Date();
            target = (today >= lastWeekViewStart && today < weekEnd)
              ? today
              : new Date(lastWeekViewStart.getTime() + 24 * 60 * 60 * 1000); // Monday
          }

          // Only redirect when the computed target differs from the day FC already landed on.
          const currentMidnight = new Date(
            currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate()
          );
          if (target && target.getTime() !== currentMidnight.getTime()) {
            navigatingToDay = true;
            lastWeekViewStart = null;
            setTimeout(() => {
              calendar.gotoDate(target);
              navigatingToDay = false;
            }, 0);
          } else {
            lastWeekViewStart = null;
          }
        }

        // Clear week-start tracking when leaving time-grid entirely (e.g. back to month).
        if (type !== 'timeGridWeek' && type !== 'timeGridDay') {
          lastWeekViewStart = null;
        }
      },
      // Fired when the async event fetch completes. Handles the case where datesRender
      // ran before events were available (initial load or uncached navigation).
      loading(isLoading) {
        if (isLoading) return;
        const { type, activeStart, activeEnd } = calendar.view;
        if (type !== 'timeGridWeek' && type !== 'timeGridDay') return;
        updateTimeRange(activeStart, activeEnd);
      },
      eventResize(info) {
        const { days, milliseconds } = info.endDelta;
        const seconds = milliseconds / 1000;
        const resizeUrl = updateUrl.replace('EVENT_ID', eventId).replace('EVENT_PART_ID', info.event.id);
        $.ajax({
          url: resizeUrl,
          data: { event_part: { end_date_time_offset: { seconds, days } } },
          dataType: 'json',
          type: 'PATCH',
          error(jqXHR) {
            const { responseText, statusText } = jqXHR;
            const errorMessage = (jqXHR.responseJSON && jqXHR.responseJSON.error)
              ? jqXHR.responseJSON.error : responseText;
            sweetAlert({ title: statusText, text: errorMessage });
            info.revert();
          }
        }).done(() => {
          $calendar.data({ reloadListView: true });
          FlashMessage.showMessageWithTimer('info', 'End time extended successfully.');
        });
      },
      eventDragStart(info) {
        // Clean up any clone left over from a previous drag that was canceled
        // before eventDrop fired (e.g. dropped on an invalid slot).
        if (duplicatedEvent) {
          calendar.getEventById(`tmp-clone-${duplicatedEvent}`)?.remove();
          duplicatedEvent = null;
        }
        if (info.jsEvent.altKey || info.jsEvent.ctrlKey) {
          duplicatedEvent = info.event.id;
          // Use a distinct temporary id so FullCalendar doesn't render two
          // events with the same id (which left a lingering artifact at the
          // source position after drop until refetchEvents resolved). The
          // temp event is removed explicitly on drop completion below.
          const cloneId = `tmp-clone-${info.event.id}`;
          calendar.addEvent({
            id: cloneId,
            title: info.event.title,
            start: info.event.start,
            end: info.event.end
          });
        }
      },
      eventDrop(info) {
        const { days, milliseconds } = info.delta;
        const seconds = milliseconds / 1000;
        if (duplicatedEvent) {
          const cloneId = `tmp-clone-${duplicatedEvent}`;
          const removeClone = () => calendar.getEventById(cloneId)?.remove();
          $.ajax({
            url: duplicateUrl.replace('EVENT_ID', eventId).replace('EVENT_PART_ID', info.event.id),
            data: { event_part: { offset: { seconds, days } } },
            dataType: 'json',
            type: 'POST',
            error(jqXHR) {
              const { responseText, statusText } = jqXHR;
              const errorMessage = (jqXHR.responseJSON && jqXHR.responseJSON.error)
                ? jqXHR.responseJSON.error : responseText;
              sweetAlert({ title: statusText, text: errorMessage });
              info.revert();
              removeClone();
            }
          }).done(() => {
            $calendar.data({ reloadListView: true });
            // Remove the visual clone before refetch so we don't leave a stale
            // event at the source position while the async refetch runs.
            // refetchEvents() rebuilds the calendar from server state.
            removeClone();
            calendar.refetchEvents();
          });
          duplicatedEvent = null;
        } else {
          sweetAlert({
            title: 'Confirmation',
            text: `You are moving "${info.event.title}" to a new start date/time. Please confirm.`,
            icon: 'warning',
            buttons: ['Cancel', 'Confirm']
          }).then((willSubmit) => {
            if (willSubmit) {
              const offsetUrl = offsetChangeUrl.replace('EVENT_ID', eventId).replace('EVENT_PART_ID', info.event.id);
              $calendar.data({ reloadListView: true });
              $.ajax({
                url: offsetUrl,
                data: { event_part: { offset: { seconds, days } } },
                dataType: 'json',
                type: 'PATCH',
                error(jqXHR) {
                  const { responseText, statusText } = jqXHR;
                  const errorMessage = (jqXHR.responseJSON && jqXHR.responseJSON.error)
                    ? jqXHR.responseJSON.error : responseText;
                  sweetAlert({ title: statusText, text: errorMessage });
                  info.revert();
                }
              });
            } else {
              info.revert();
            }
          });
        }
      },
      eventClick(info) {
        SidePanel.show();
        SidePanel.load(editUrl.replace('EVENT_PART_ID', info.event.id));
      },
      eventRender(info) {
        const tagDots = info.event.extendedProps.tag_dots;
        if (tagDots && tagDots.length > 0) {
          info.el.querySelectorAll('.fc-tag-dots').forEach(el => el.remove());
          const container = document.createElement('div');
          container.className = 'fc-tag-dots';
          tagDots.slice(0, 3).forEach(hex => {
            const dot = document.createElement('span');
            dot.className = 'fc-tag-dot';
            dot.style.backgroundColor = hex;
            container.appendChild(dot);
          });
          info.el.appendChild(container);
        }
        const titleMatches = info.event.title.toLowerCase().indexOf(search) > -1;
        const eventTagIds = (info.event.extendedProps.tag_ids || []).map(String);
        const tagMatches = selectedTagIds.size === 0
          || eventTagIds.some(id => selectedTagIds.has(id));
        return titleMatches && tagMatches;
      },
      select(info) {
        const { allDay, startStr, endStr } = info;
        let start = null;
        let end = null;
        if (allDay) {
          start = moment(startStr).hours(9);
          end = moment(endStr).subtract(1, 'second').hours(10).minutes(0)
            .seconds(0);
        } else {
          start = moment(startStr);
          end = moment(endStr).seconds(0);
        }

        const paramsData = {
          start: start.format('YYYY-MM-DD[T]HH:mm:ss'),
          end: end.format('YYYY-MM-DD[T]HH:mm:ss')
        };

        SidePanel.loadFromAjax({ url: newEventPartUrl, data: paramsData });
      }
    });

    // Computes the desired time range from schedule activities in [viewStart, viewEnd)
    // and applies it via setOption if it differs from what's currently set.
    // Deferred with setTimeout to avoid mutating calendar state mid-render-cycle.
    function updateTimeRange(viewStart, viewEnd) {
      const range = computeDynamicTimeRange(calendar.getEvents(), viewStart, viewEnd);
      const targetMin = range ? range.minTime : MIN_TIME;
      const targetMax = range ? range.maxTime : MAX_TIME;
      if (targetMin === currentMinTime && targetMax === currentMaxTime) return;
      currentMinTime = targetMin;
      currentMaxTime = targetMax;
      setTimeout(() => {
        calendar.setOption('minTime', targetMin);
        calendar.setOption('maxTime', targetMax);
      }, 0);
    }

    $('.js-search-input').on('keyup change', (event) => {
      clearTimeout(debounce);
      debounce = setTimeout(() => {
        search = $(event.target).val().toLowerCase();
        calendar.rerenderEvents();
        // Show a hint when an active search filters out all visible activities.
        // Uses setTimeout(0) to check the DOM after rerenderEvents completes.
        setTimeout(() => {
          $noResults.toggle(search !== '' && $calendar.find('.fc-event').length === 0);
        }, 0);
      }, 400);
    });

    // Tag filter — mirrors event_parts_table.js#watchTagFilter so list and
    // calendar views share UX. Filter state is in-memory only (not persisted),
    // matching the calendar's existing search-input behavior.
    const updateTagFilterUI = () => {
      const count = selectedTagIds.size;
      $('.js-tag-filter-counter').text(count).toggleClass('is-hidden', count === 0);
      $('.js-tag-filter-clear').toggleClass('is-hidden', count === 0);
    };

    $('.js-tag-filter-dropdown .dropdown__list').on('click', (e) => {
      e.stopPropagation();
    });

    $('.js-tag-filter-check').on('change', (e) => {
      const tagId = String($(e.currentTarget).data('tag-id'));
      if (e.currentTarget.checked) {
        selectedTagIds.add(tagId);
      } else {
        selectedTagIds.delete(tagId);
      }
      updateTagFilterUI();
      calendar.rerenderEvents();
    });

    $('.js-tag-filter-clear').on('click', (e) => {
      e.preventDefault();
      selectedTagIds.clear();
      $('.js-tag-filter-check').prop('checked', false);
      updateTagFilterUI();
      calendar.rerenderEvents();
    });

    calendar.render();

    // Scale button clicks — update localStorage, apply to calendar, and refresh button state.
    $(document).on('click', '.js-scale-btn', (e) => {
      const scale = $(e.currentTarget).data('scale');
      const { type } = calendar.view;
      if (type === 'timeGridWeek') {
        window.localStorage.setItem(weekScaleKey, scale);
      } else if (type === 'timeGridDay') {
        window.localStorage.setItem(dayScaleKey, scale);
      }
      const cfg = SCALE_CONFIG[scale];
      calendar.setOption('slotDuration', cfg.slotDuration);
      calendar.setOption('slotLabelInterval', cfg.slotLabelInterval);
      // Sync active state immediately (datesRender won't re-fire for setOption alone).
      $('.js-scale-btn').each(function syncBtn() {
        const btnScale = $(this).data('scale');
        $(this).toggleClass('is-on', btnScale === scale)
               .toggleClass('is-off', btnScale !== scale);
      });
    });

    // Injected after render so it sits below the calendar grid, inside the same container.
    const $noResults = $('<p class="js-calendar-no-results" style="display:none;text-align:center;padding:1.5rem 0;color:#888;">No activities found for your search.</p>');
    $calendar.after($noResults);

    $calendar.on('resize', () => {
      setTimeout(() => {
        calendar.updateSize();
        calendar.scrollToTime(SCROLL_TO_TIME);
      }, 200);
    });
  }
};

export default EventPartsCalendar;
